import { BuildingOffice2Icon, CalendarDaysIcon, ChartBarIcon, ChatBubbleLeftIcon, ChatBubbleLeftRightIcon, ClipboardDocumentListIcon, FlagIcon, HeartIcon, RectangleGroupIcon, ShareIcon, StarIcon, UsersIcon } from "@heroicons/react/24/outline";
import { PERFORMANCE_CYCLES_ACCESS } from "@packages/core/access/settingsAccess.ts";
import { hasProduct } from "@packages/core/session/products.ts";
import { hasAnyRole } from "@packages/core/session/roles.ts";
import type { AppDeferredRegistrationData, AppModuleRegisterFunction } from "@packages/core-client/registration/appModuleRegisterFunction.ts";
import { getCommandMenuLinksRegistryPlugin } from "@packages/core-client/registration/command-menu/CommandMenuLinksPlugin.ts";
import { registerArea } from "@packages/core-client/registration/registerArea.tsx";
import { initFeedbackI18next } from "@domains/feedback/client/i18n/i18next.ts";
import { initMeetingsI18next } from "@domains/meetings/client/i18n/i18next.ts";
import { isMeetingsInPerformanceEnabled } from "@domains/meetings/client/utils/meetingsInPerformanceAccess.ts";
import { REVIEW_GOOD_VIBE_SEGMENT } from "@domains/reviews/client/components/reviewRoutes.ts";
import { initReviewCyclesI18next } from "@domains/reviews/client/i18n/i18next.ts";
import type { FireflyRuntime } from "@squide/firefly";
import type { i18n } from "i18next";
import { ORGANIZATION_FEEDBACK_INBOX_ACCESS, ORGANIZATION_GOALS_ACCESS } from "#shared/access.ts";
import { initI18next } from "./i18n/i18next.ts";
import { ProfileErrorBoundary } from "./pages/directory/profile/ProfileErrorBoundary.tsx";
import { ProfileTabsHeading } from "./pages/directory/profile/ProfileTabsHeading.tsx";
import { useIsProfileTabHidden } from "./pages/directory/profile/useIsProfileTabHidden.ts";
import { useIsProfileTabsPending } from "./pages/directory/profile/useIsProfileTabsPending.ts";
import { useRenderProfileAsTitleOnly } from "./pages/directory/profile/useRenderProfileAsTitleOnly.ts";

function registerProfileRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const profileArea = registerArea(runtime, "employee-profile", "/org/employees/:personId", UsersIcon, i18nextInstance);

    const profileTabs = profileArea.registerTabsArea("main", {
        Heading: ProfileTabsHeading,
        hideTitle: true,
        errorElement: <ProfileErrorBoundary />,
        commandMenu: false,
        useIsTabHidden: useIsProfileTabHidden,
        useTabsPending: useIsProfileTabsPending,
        useRenderAsTitleOnly: useRenderProfileAsTitleOnly
    });

    const about = profileTabs.registerRoute("about", {
        index: true,
        page: "DirectoryProfileAbout",
        lazy: async () => {
            const { ProfileAboutPage } = await import("./pages/directory/profile/about/ProfileAboutPage.tsx");

            return { element: <ProfileAboutPage /> };
        }
    });

    const goals = profileTabs.registerRoute("goals", {
        path: "goals",
        page: "DirectoryProfileGoals",
        lazy: async () => {
            const { ProfileGoalsPage } = await import("./pages/directory/profile/goals/ProfileGoalsPage.tsx");

            return { element: <ProfileGoalsPage /> };
        }
    });

    goals.registerOverlay("progress-update", { segment: "update/:goalId", page: "DirectoryProfileGoalsProgressUpdate", lazy: loadGoalProgressUpdateRoute });

    const reviews = profileTabs.registerRoute("reviews", {
        path: "reviews",
        page: "DirectoryProfileReviews",
        lazy: async () => {
            const { ProfileReviewsPage } = await import("./pages/directory/profile/reviews/ProfileReviewsPage.tsx");

            return { element: <ProfileReviewsPage /> };
        }
    });

    profileTabs.registerRoute("timeline-redirect", {
        path: "timeline/*",
        lazy: async () => {
            const { ProfileTimelineRedirect } = await import("./pages/directory/profile/ProfileTimelineRedirect.tsx");

            return { element: <ProfileTimelineRedirect /> };
        }
    });

    const goodVibes = profileTabs.registerRoute("good-vibes", {
        path: "good-vibes",
        page: "DirectoryProfileGoodVibes",
        lazy: async () => {
            const { KeyedProfileGoodVibesPage } = await import("./pages/directory/profile/good-vibes/ProfileGoodVibesPage.tsx");

            return { element: <KeyedProfileGoodVibesPage /> };
        }
    });

    goodVibes.registerOverlay("detail", {
        segment: ":recognitionId",
        page: "DirectoryProfileGoodVibeDetail",
        lazy: async () => {
            const { GoodVibeDetailRoute } = await import("./pages/directory/profile/good-vibes/GoodVibeDetailRoute.tsx");

            return { element: <GoodVibeDetailRoute /> };
        }
    });

    const meetings = profileTabs.registerRoute("meetings", {
        path: "meetings",
        page: "DirectoryProfileMeetings",
        lazy: async () => {
            const { KeyedProfileMeetingsPage } = await import("./pages/directory/profile/meetings/ProfileMeetingsPage.tsx");

            return { element: <KeyedProfileMeetingsPage /> };
        }
    });

    const engagement = profileTabs.registerRoute("engagement", {
        path: "engagement",
        page: "DirectoryProfileEngagement",
        lazy: async () => {
            const { ProfileEngagementPage } = await import("./pages/directory/profile/engagement/ProfileEngagementPage.tsx");

            return { element: <ProfileEngagementPage /> };
        }
    });

    engagement.registerOverlay("good-vibe-detail", {
        segment: "good-vibes/:recognitionId",
        page: "DirectoryProfileEngagementGoodVibeDetail",
        lazy: async () => {
            const { EngagementGoodVibeDetailRoute } = await import("./pages/directory/profile/engagement/EngagementGoodVibeDetailRoute.tsx");

            return { element: <EngagementGoodVibeDetailRoute /> };
        }
    });

    const progression = profileTabs.registerRoute("progression", {
        path: "progression",
        page: "DirectoryProfileProgression",
        lazy: async () => {
            const { ProfileProgressionPage } = await import("./pages/directory/profile/progression/ProfileProgressionPage.tsx");

            return { element: <ProfileProgressionPage /> };
        }
    });

    about.registerLink({ priority: 1000 });
    goodVibes.registerLink({ priority: 998 });
    reviews.registerLink({ priority: 996 });
    goals.registerLink({ priority: 995 });

    return { meetings, engagement, progression };
}

function registerReviewDetailsRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const reviewArea = registerArea(runtime, "employee-review", "/org/employees/:personId/reviews/:cycleId", StarIcon, i18nextInstance);

    reviewArea.registerPage({
        page: "DirectoryProfileReviewDetails",
        lazy: async () => {
            const { ReviewDetailsPage } = await import("./pages/directory/profile/reviews/ReviewDetailsPage.tsx");

            return { element: <ReviewDetailsPage /> };
        }
    });
}

function registerProgressionReviewsRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const progressionReviewsArea = registerArea(runtime, "employee-progression-reviews", "/org/employees/:personId/progression/reviews", StarIcon, i18nextInstance);

    progressionReviewsArea.registerPage({
        page: "DirectoryProfileProgressionReviews",
        lazy: async () => {
            const { ProfileReviewsListPage } = await import("./pages/directory/profile/reviews/ProfileReviewsListPage.tsx");

            return { element: <ProfileReviewsListPage />, errorElement: <ProfileErrorBoundary /> };
        }
    });
}

function registerReviewShareRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const reviewShareArea = registerArea(runtime, "employee-review-share", "/org/employees/:personId/reviews/:cycleId/share", StarIcon, i18nextInstance);

    reviewShareArea.registerPage({
        page: "DirectoryProfileReviewShare",
        lazy: async () => {
            const { ReviewSharePage } = await import("./pages/directory/profile/reviews/ReviewSharePage.tsx");

            return { element: <ReviewSharePage /> };
        }
    });
}

function registerReviewWriteRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const reviewWriteArea = registerArea(runtime, "employee-review-write", "/org/employees/:personId/reviews/:cycleId/:perspective", StarIcon, i18nextInstance);

    reviewWriteArea.registerPage({
        page: "DirectoryProfileReviewWrite",
        fullHeight: true,
        lazy: async () => {
            const { ReviewWritePage } = await import("./pages/directory/profile/reviews/ReviewWritePage.tsx");

            return { element: <ReviewWritePage /> };
        }
    });

    reviewWriteArea.registerOverlay("good-vibe-detail", {
        segment: `${REVIEW_GOOD_VIBE_SEGMENT}/:recognitionId`,
        page: "DirectoryProfileReviewWriteGoodVibeDetail",
        lazy: async () => {
            const { ReviewWriteGoodVibeRoute } = await import("./pages/directory/profile/reviews/ReviewWriteGoodVibeRoute.tsx");

            return { element: <ReviewWriteGoodVibeRoute /> };
        }
    });
}

function registerMeetingsRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const meetingArea = registerArea(runtime, "employee-meeting", "/org/employees/:personId/meetings/:meetingId", CalendarDaysIcon, i18nextInstance);

    meetingArea.registerPage({
        page: "DirectoryMeetingDetail",
        fullHeight: true,
        lazy: async () => {
            const { MeetingDetailPage } = await import("./pages/meetings/detail/MeetingDetailPage.tsx");
            const { MeetingErrorBoundary } = await import("./pages/meetings/detail/MeetingErrorBoundary.tsx");

            return { element: <MeetingDetailPage />, errorElement: <MeetingErrorBoundary /> };
        }
    });
}

function registerGoalEditRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const goalEditArea = registerArea(runtime, "employee-goal-edit", "/org/employees/:personId/goals/:goalId/edit", FlagIcon, i18nextInstance);

    goalEditArea.registerPage({
        page: "DirectoryGoalEdit",
        fullHeight: true,
        lazy: async () => {
            const { GoalEditPage } = await import("./pages/goals/edit/GoalEditPage.tsx");
            const { GoalErrorBoundary } = await import("./pages/goals/detail/GoalErrorBoundary.tsx");

            return { element: <GoalEditPage />, errorElement: <GoalErrorBoundary /> };
        }
    });
}

async function loadGoalProgressUpdateRoute() {
    const { GoalProgressUpdateRoute } = await import("./pages/goals/detail/GoalProgressUpdateRoute.tsx");

    return { element: <GoalProgressUpdateRoute /> };
}

function registerProgressionGoalsRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const progressionGoalsArea = registerArea(runtime, "employee-progression-goals", "/org/employees/:personId/progression/goals", FlagIcon, i18nextInstance);

    progressionGoalsArea.registerPage({
        page: "DirectoryProfileProgressionGoals",
        lazy: async () => {
            const { ProgressionGoalsListPage } = await import("./pages/directory/profile/progression/goals/ProgressionGoalsListPage.tsx");

            return { element: <ProgressionGoalsListPage />, errorElement: <ProfileErrorBoundary /> };
        }
    });

    progressionGoalsArea.registerOverlay("progress-update", { segment: "update/:goalId", page: "DirectoryProfileProgressionGoalsProgressUpdate", lazy: loadGoalProgressUpdateRoute });
}

function registerEngagementFeedbackRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const engagementFeedbackArea = registerArea(runtime, "employee-engagement-feedback", "/org/employees/:personId/engagement/feedback", ChatBubbleLeftRightIcon, i18nextInstance);

    engagementFeedbackArea.registerPage({
        page: "DirectoryProfileEngagementFeedback",
        lazy: async () => {
            const { EngagementFeedbackListPage } = await import("./pages/directory/profile/engagement/feedback/EngagementFeedbackListPage.tsx");

            return { element: <EngagementFeedbackListPage />, errorElement: <ProfileErrorBoundary /> };
        }
    });

    runtime.registerRoute({ path: "/org/employees/:personId/engagement/feedback/:feedbackId", element: null }, { parentPath: "/org/employees/:personId/engagement/feedback" });
}

function registerEngagementMetricsRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const engagementMetricsArea = registerArea(runtime, "employee-engagement-metrics", "/org/employees/:personId/engagement/metrics", ChartBarIcon, i18nextInstance);

    engagementMetricsArea.registerPage({
        page: "DirectoryProfileEngagementMetrics",
        lazy: async () => {
            const { ProfileEngagementMetricsPage } = await import("./pages/directory/profile/engagement/metrics/ProfileEngagementMetricsPage.tsx");

            return { element: <ProfileEngagementMetricsPage />, errorElement: <ProfileErrorBoundary /> };
        }
    });
}

function registerGoalsRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const goalArea = registerArea(runtime, "employee-goal", "/org/employees/:personId/goals/:goalId", FlagIcon, i18nextInstance);

    goalArea.registerPage({
        page: "DirectoryGoalDetail",
        lazy: async () => {
            const { GoalDetailPage } = await import("./pages/goals/detail/GoalDetailPage.tsx");
            const { GoalErrorBoundary } = await import("./pages/goals/detail/GoalErrorBoundary.tsx");

            return { element: <GoalDetailPage />, errorElement: <GoalErrorBoundary /> };
        }
    });

    goalArea.registerOverlay("progress-update", { segment: "update", page: "DirectoryGoalProgressUpdate", lazy: loadGoalProgressUpdateRoute });
    goalArea.registerOverlay("progress-update-edit", { segment: "update/:updateId", page: "DirectoryGoalProgressUpdate", lazy: loadGoalProgressUpdateRoute });
}

function registerOrganizationGoalRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const goalArea = registerArea(runtime, "organization-goal", "/org/goals/:goalId", FlagIcon, i18nextInstance, { access: ORGANIZATION_GOALS_ACCESS });

    goalArea.registerPage({
        page: "OrganizationGoalDetail",
        lazy: async () => {
            const { GoalDetailPage } = await import("./pages/goals/detail/GoalDetailPage.tsx");
            const { GoalErrorBoundary } = await import("./pages/goals/detail/GoalErrorBoundary.tsx");

            return { element: <GoalDetailPage />, errorElement: <GoalErrorBoundary /> };
        }
    });

    goalArea.registerOverlay("progress-update", { segment: "update", page: "OrganizationGoalProgressUpdate", lazy: loadGoalProgressUpdateRoute });
    goalArea.registerOverlay("progress-update-edit", { segment: "update/:updateId", page: "OrganizationGoalProgressUpdate", lazy: loadGoalProgressUpdateRoute });

    const goalEditArea = registerArea(runtime, "organization-goal-edit", "/org/goals/:goalId/edit", FlagIcon, i18nextInstance, { access: ORGANIZATION_GOALS_ACCESS });

    goalEditArea.registerPage({
        page: "OrganizationGoalEdit",
        fullHeight: true,
        lazy: async () => {
            const { GoalEditPage } = await import("./pages/goals/edit/GoalEditPage.tsx");
            const { GoalErrorBoundary } = await import("./pages/goals/detail/GoalErrorBoundary.tsx");

            return { element: <GoalEditPage />, errorElement: <GoalErrorBoundary /> };
        }
    });
}

async function registerMsw(runtime: FireflyRuntime) {
    if (runtime.isMswEnabled) {
        const requestHandlers = (await import("./api/mocks/getRequestHandlers.ts")).getRequestHandlers();

        runtime.registerRequestHandlers(requestHandlers);
    }
}

export const registerOrganization: AppModuleRegisterFunction = async runtime => {
    const i18nextInstance = initI18next(runtime);
    initMeetingsI18next(runtime);
    initFeedbackI18next(runtime);
    initReviewCyclesI18next(runtime);

    await registerMsw(runtime);

    const area = registerArea(runtime, "organization", "/org", BuildingOffice2Icon, i18nextInstance);
    const mainPage = area.registerTabsArea("main");

    const overview = mainPage.registerRoute("overview", {
        index: true,
        page: "OrganizationOverview",
        lazy: async () => {
            const { OrganizationOverviewPage } = await import("./pages/overview/OrganizationOverviewPage.tsx");

            return { element: <OrganizationOverviewPage /> };
        }
    });

    const employees = mainPage.registerRoute("employees", {
        path: "/employees",
        page: "Directory",
        lazy: async () => {
            const { PeopleListPage } = await import("./pages/directory/list/PeopleListPage.tsx");

            return { element: <PeopleListPage /> };
        }
    });

    const goodVibes = mainPage.registerRoute("good-vibes", {
        path: "/good-vibes",
        page: "OrganizationGoodVibes",
        lazy: async () => {
            const { OrganizationGoodVibesPage } = await import("./pages/good-vibes/OrganizationGoodVibesPage.tsx");

            return { element: <OrganizationGoodVibesPage /> };
        }
    });

    goodVibes.registerOverlay("detail", {
        segment: ":recognitionId",
        page: "OrganizationGoodVibeDetail",
        lazy: async () => {
            const { OrganizationGoodVibeDetailRoute } = await import("./pages/good-vibes/OrganizationGoodVibeDetailRoute.tsx");

            return { element: <OrganizationGoodVibeDetailRoute /> };
        }
    });

    const goals = mainPage.registerRoute("goals", {
        path: "/goals",
        page: "OrganizationGoals",
        access: ORGANIZATION_GOALS_ACCESS,
        lazy: async () => {
            const { OrganizationGoalsPage } = await import("./pages/goals/list/OrganizationGoalsPage.tsx");

            return { element: <OrganizationGoalsPage /> };
        }
    });

    goals.registerOverlay("progress-update", { segment: "update/:goalId", page: "OrganizationGoalsProgressUpdate", lazy: loadGoalProgressUpdateRoute });

    const reviewCycles = mainPage.registerRoute("review-cycles", {
        path: "/review-cycles",
        page: "OrganizationReviewCycles",
        access: PERFORMANCE_CYCLES_ACCESS,
        lazy: async () => {
            const { OrganizationReviewCyclesPage } = await import("./pages/review-cycles/list/OrganizationReviewCyclesPage.tsx");

            return { element: <OrganizationReviewCyclesPage /> };
        }
    });

    const feedback = mainPage.registerRoute("feedback", {
        path: "/feedback",
        page: "OrganizationFeedback",
        access: ORGANIZATION_FEEDBACK_INBOX_ACCESS,
        lazy: async () => {
            const { OrganizationFeedbackPage } = await import("./pages/feedback/OrganizationFeedbackPage.tsx");

            return { element: <OrganizationFeedbackPage /> };
        }
    });

    runtime.registerRoute({ path: "/org/feedback/:feedbackId", element: null }, { parentPath: "/org/feedback" });

    const orgChart = mainPage.registerRoute("org-chart", {
        page: "OrgChart",
        fullHeight: true,
        lazy: async () => {
            const { OrgChartPage } = await import("./pages/org-chart/OrgChartPage.tsx");

            return { element: <OrgChartPage /> };
        }
    });

    const { meetings, engagement, progression } = registerProfileRoutes(runtime, i18nextInstance);
    registerReviewDetailsRoutes(runtime, i18nextInstance);
    registerProgressionReviewsRoutes(runtime, i18nextInstance);
    registerReviewShareRoutes(runtime, i18nextInstance);
    registerReviewWriteRoutes(runtime, i18nextInstance);
    registerMeetingsRoutes(runtime, i18nextInstance);
    registerGoalsRoutes(runtime, i18nextInstance);
    registerGoalEditRoutes(runtime, i18nextInstance);
    registerProgressionGoalsRoutes(runtime, i18nextInstance);
    registerEngagementFeedbackRoutes(runtime, i18nextInstance);
    registerEngagementMetricsRoutes(runtime, i18nextInstance);
    registerOrganizationGoalRoutes(runtime, i18nextInstance);

    return (deferredRuntime: FireflyRuntime, { session }: AppDeferredRegistrationData) => {
        engagement.registerLink({ priority: 993 });
        progression.registerLink({ priority: 992 });

        if (isMeetingsInPerformanceEnabled(deferredRuntime.getFeatureFlag("meetings-in-performance", false), session)) {
            meetings.registerLink({ priority: 991 });
        }

        const isRedesignEnabled = deferredRuntime.getFeatureFlag("navigation-redesign", false);

        overview.registerLink({ priority: 1000, icon: RectangleGroupIcon, commandMenu: isRedesignEnabled });
        employees.registerLink({ priority: 999, icon: UsersIcon, commandMenu: isRedesignEnabled });

        if (session?.memberId && hasProduct(session, "wov")) {
            goodVibes.registerLink({ priority: 998, icon: HeartIcon, commandMenu: isRedesignEnabled });
        }

        if (hasAnyRole(session, ORGANIZATION_GOALS_ACCESS)) {
            goals.registerLink({ priority: 997, icon: FlagIcon, commandMenu: isRedesignEnabled });
        }

        if (hasAnyRole(session, PERFORMANCE_CYCLES_ACCESS)) {
            reviewCycles.registerLink({ priority: 996, icon: ClipboardDocumentListIcon, commandMenu: isRedesignEnabled, limitedVisibility: true });
        }

        if (!isRedesignEnabled) {
            return;
        }

        if (hasAnyRole(session, ORGANIZATION_FEEDBACK_INBOX_ACCESS) && hasProduct(session, "wov")) {
            feedback.registerLink({ priority: 997, icon: ChatBubbleLeftIcon, commandMenu: true, limitedVisibility: true });
        }

        area.registerToTopNav({ priority: 125 });

        if (deferredRuntime.getFeatureFlag("org-chart-viewer", false)) {
            orgChart.registerLink({ priority: 997, icon: ShareIcon });
        }

        if (session?.memberId && hasProduct(session, "wpm")) {
            const paletteMemberId = session.memberId;

            getCommandMenuLinksRegistryPlugin(deferredRuntime)?.registerLink({
                id: "employee-progression-goals-palette",
                to: `/org/employees/${paletteMemberId}/progression/goals`,
                groupId: "organization",
                icon: FlagIcon,
                priority: 500,
                i18next: {
                    instance: i18nextInstance,
                    namespace: "Navigation",
                    labelKey: "employee-progression-goals.label",
                    descriptionKey: "employee-progression-goals.description",
                    parentLabelKey: "organization.label",
                    groupLabelKey: "organization.label"
                }
            });

            getCommandMenuLinksRegistryPlugin(deferredRuntime)?.registerLink({
                id: "employee-progression-reviews-palette",
                to: `/org/employees/${paletteMemberId}/progression/reviews`,
                groupId: "organization",
                icon: StarIcon,
                priority: 499,
                i18next: {
                    instance: i18nextInstance,
                    namespace: "Navigation",
                    labelKey: "employee-progression-reviews.label",
                    descriptionKey: "employee-progression-reviews.description",
                    parentLabelKey: "organization.label",
                    groupLabelKey: "organization.label"
                }
            });
        }
    };
};
